<script lang="ts" setup>
import {
  ref,
  computed,
  useTemplateRef,
  usePlaces,
  useSpots,
  useMaps,
  useCreators,
  watch,
} from "#imports";
import { onClickOutside, watchDebounced } from "@vueuse/core";
import MPEmptyState from "../layouts/MPEmptyState.vue";
import MPLoader from "../widgets/MPLoader.vue";
import MPToggle from "./MPToggle.vue";
import MPImg from "../widgets/MPImg.vue";

const props = withDefaults(
  defineProps<{
    placeholder?: string;
  }>(),
  {
    placeholder: "Search",
  },
);

const placeStore = usePlaces();
const spotStore = useSpots();
const mapStore = useMaps();
const creatorStore = useCreators();

const searchTerm = ref<string>("");
const isLoading = ref<boolean>(false);
type TabMenu = "spots" | "maps" | "creators" | "destinations";
const activeMenu = ref<TabMenu>("spots");

const hasPlaces = computed(() => (placeStore.searchResults?.length ?? 0) > 0);
const hasSpots = computed(() => (spotStore.searchResults?.length ?? 0) > 0);
const hasCreators = computed(
  () => (creatorStore.searchResults?.length ?? 0) > 0,
);
const hasMaps = computed(() => (mapStore.searchResults?.length ?? 0) > 0);

const hasResults = computed(
  () => hasPlaces.value || hasSpots.value || hasCreators.value || hasMaps.value,
);
const firstResult = computed<TabMenu | null>(() => {
  if (hasSpots.value) return "spots";
  if (hasCreators.value) return "creators";
  if (hasMaps.value) return "maps";
  if (hasPlaces.value) return "destinations";
  return null;
});

watch(searchTerm, () => {
  // immediately set loading, but debounce searching
  isLoading.value = true;
});

watchDebounced(
  searchTerm,
  async () => {
    const search = searchTerm.value.trim();
    await Promise.all([
      placeStore.search({ search, limit: 20, withTotal: true }),
      spotStore.search({ search, limit: 20, withTotal: true }),
      creatorStore.search({ search, limit: 20, withTotal: true }),
      mapStore.search({ search, limit: 20, withTotal: true }),
    ]);

    if (
      (activeMenu.value === "creators" && !hasCreators.value) ||
      (activeMenu.value === "spots" && !hasSpots.value) ||
      (activeMenu.value === "maps" && !hasMaps.value) ||
      (activeMenu.value === "destinations" && !hasPlaces.value)
    ) {
      // if active menu has no results, select one that does
      activeMenu.value = firstResult.value ?? "creators";
    }

    isLoading.value = false;
  },
  { debounce: 250 },
);

const toggleDropdown = ref(false);
const searchContainer = useTemplateRef("searchContainer");
onClickOutside(searchContainer, () => {
  toggleDropdown.value = false;
});
</script>

<template>
  <div ref="searchContainer" class="search-container">
    <div class="search-input">
      <input
        v-model="searchTerm"
        :placeholder="props.placeholder"
        @focus="toggleDropdown = true"
        :class="{ active: toggleDropdown }"
      />
    </div>
    <TransitionGroup
      tag="div"
      name="transition-fade"
      :class="['search-results', { active: toggleDropdown }]"
    >
      <div v-if="isLoading" class="status" key="2">
        <MPLoader label="Searching..." />
      </div>
      <div class="status" key="1" v-else-if="!hasResults && !searchTerm">
        <MPEmptyState
          title="Search"
          subtitle="Search for places, creators, destinations & more"
          imageSrc="/images/empty-state-maps.png"
        />
      </div>

      <MPEmptyState
        v-else-if="searchTerm && !hasResults"
        title="No results found"
        videoSrc="/media/videos/empty-state-saved-spots.webm"
      />
      <template v-else>
        <div class="search-results-menu" key="3">
          <MPToggle
            v-if="hasSpots"
            label="Spots"
            :badge="spotStore.searchTotalRows"
            :selected="activeMenu === 'spots'"
            @click="activeMenu = 'spots'"
          />
          <MPToggle
            v-if="hasMaps"
            label="Maps"
            :badge="mapStore.searchTotalRows"
            :selected="activeMenu === 'maps'"
            @click="activeMenu = 'maps'"
          />
          <MPToggle
            v-if="hasCreators"
            label="Creators"
            :badge="creatorStore.searchTotalRows"
            :selected="activeMenu === 'creators'"
            @click="activeMenu = 'creators'"
          />
          <MPToggle
            v-if="hasPlaces"
            label="Destinations"
            :badge="placeStore.searchTotalRows"
            :selected="activeMenu === 'destinations'"
            @click="activeMenu = 'destinations'"
          />
        </div>
        <div class="search-results-content" key="4">
          <TransitionGroup name="transition-list" tag="div">
            <div v-if="activeMenu === 'spots'" key="spots">
              <ul>
                <li v-for="spot in spotStore.searchResults" :key="spot.id">
                  <NuxtLinkLocale :to="`/maps/${spot.map_id}/spots/${spot.id}`">
                    <MPImg
                      :src="spot.cover_image_url!"
                      :width="35"
                      :height="35"
                    />
                    <span>
                      {{ spot.name }}<br />
                      <small v-if="spot.summary">
                        {{ spot.summary.split(" ").slice(0, 5).join(" ") }}...
                      </small>
                    </span>
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
            <div v-if="activeMenu === 'creators'" key="creators">
              <ul>
                <li
                  v-for="creator in creatorStore.searchResults"
                  :key="creator.id"
                >
                  <NuxtLinkLocale :to="`/@${creator.mappr_handle}`">
                    <MPImg
                      :src="
                        creator.profile_image_url ??
                        '/media/images/placeholder-user.webp'
                      "
                      :width="35"
                      :height="35"
                      style="width: 35px; height: 35px"
                    />
                    <span>
                      @{{ creator.mappr_handle }}<br />
                      <small v-if="creator.bio_text">
                        {{
                          creator.bio_text.split(" ").slice(0, 5).join(" ")
                        }}...
                      </small>
                    </span>
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
            <div v-if="activeMenu === 'destinations'" key="destinations">
              <ul>
                <li
                  v-for="destination in placeStore.searchResults"
                  :key="destination.id"
                >
                  <NuxtLinkLocale :to="`/destinations/${destination.id}`">
                    <span v-if="destination.place">
                      {{ destination.place }}<br />
                      <small>{{ destination.regionCountry }}</small>
                    </span>
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
            <div v-if="activeMenu === 'maps'" key="maps">
              <ul>
                <li v-for="map in mapStore.searchResults" :key="map.id">
                  <NuxtLinkLocale :to="`/maps/${map.current_slug}`">
                    <MPImg
                      :src="map.cover_image_url!"
                      :width="35"
                      :height="35"
                    />
                    <span>
                      {{ map.title }}<br />
                      <small v-if="map.summary">
                        {{ map.summary.split(" ").slice(0, 5).join(" ") }}...
                      </small>
                    </span>
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
          </TransitionGroup>
        </div>
      </template>
    </TransitionGroup>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/scrollbar.pcss";
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/animations.pcss";

.search-container {
  position: relative;
  height: 80dvh;
  overflow: hidden;
  @mixin screen md {
    height: auto;
    overflow: visible;
  }
}

.status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.search-results {
  height: 100%;
  @mixin screen md {
    display: none;
    position: absolute;
    background-color: white;
    border-radius: 0.5rem;
    width: 25rem;
    height: 26rem;
    top: 3.5rem;
    right: 0;
    overflow: hidden;
    box-shadow: var(--shadow-base);
    z-index: 8500;
  }
}

.search-results.active {
  display: block;
}

.search-results-menu {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  overflow-x: scroll;
  @mixin scrollbar-reset;
}

.search-results-content {
  padding: 0.5rem 0.5rem 2rem 0.5rem;
  display: flex;
  overflow-y: scroll;
  height: calc(100% - 3.5rem);
  @mixin scrollbar-reset;
  max-height: calc(70dvh - 0.5rem);
  @mixin screen md {
    height: calc(100% - 50px); /* Minus the height of the menu  */
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    padding-bottom: 1rem;
    li a {
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.25rem 0.5rem;
      align-items: center;
      line-height: 1.2;
      img {
        border-radius: 50%;
        min-width: 35px;
      }
      small {
        display: block;
        font-size: 0.875rem;
        color: var(--color-slate-500);
      }
    }
  }
}

.search-input {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: left;
  margin: 0.5rem 0.5rem 0 0.5rem;
  @mixin screen md {
    margin: 0;
  }

  &:before {
    font-family: "remixicon";
    content: "\F0CD";
    position: absolute;
    color: var(--color-slate-400);
    left: 0.75rem;
    font-size: 1.125rem;
    align-items: center;
  }
  input {
    padding: 0.5rem 1rem 0.5rem 2.25rem;
    font-size: 1rem;
    width: 100%;
    border: 1px solid var(--color-slate-100);
    background: var(--color-slate-50);
    color: var(--color-slate-700);
    outline: none;
    border-radius: 0.75rem;
    height: 2.85rem;
    @mixin screen md {
      border-radius: 1rem;
    }
  }
  input::placeholder {
    font-size: 1rem;
    color: var(--color-slate-400);
    padding: 0;
  }
  input:focus {
    outline: none;
    border-color: var(--color-indigo-300);
  }
}

img {
  width: 35px;
  height: 35px;
}
</style>
